import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

export default class ErasGallery {
	constructor(section) {
		this.section = section;
		this.gallery = this.section.querySelector('.eras-gallery');
		this.panels = [...this.gallery.querySelectorAll('.eras-panel')];
		this.captions = [...this.gallery.querySelectorAll('.more-info')];
		this.header = document.querySelector('.main-navbar');
		this.triggers = [];


		this.mm = gsap.matchMedia();
		this._hoverCleanups = [];
		this._closeFns = new Map();

		this.init();
		this.initResize();
		this.initCaptionHover();
		this.initScrollClose();
	}

	init() {
		const total = this.panels.length;

		this.mm.add({
			isMobile: '(max-width: 599px)',
			isTablet: '(min-width: 600px) and (max-width: 1023px)',
			isDesktop: '(min-width: 1024px)',
		}, (context) => {
			
			const { isMobile } = context.conditions;
			const headerH = this.header ? this.header.offsetHeight : 85;
			


			this.panels.forEach((panel, i) => {
				const pic = panel.querySelector('picture');
				const img = pic?.querySelector('img');
				const caption = panel.querySelector('.more-info');
				const isLast = i === total - 1;
				const nextPanel = !isLast ? this.panels[i + 1] : null;

				// Z-index decrescente: pannello corrente (pinnato) sta sopra il successivo
				gsap.set(panel, { zIndex: total - i });

				// Opacity e blur animano sulla picture
				gsap.to(pic, {
					opacity: 1,
					filter: 'blur(0px)',
					ease: 'none',
					scrollTrigger: {
						trigger: panel,
						start: 'top bottom',
						end: 'top 60%',
						scrub: true,
					}
				});

				// Scale anima sull'img (il transform CSS è sull'img, non sulla picture)
				if (img) {
					gsap.to(img, {
						scale: 1,
						ease: 'power2.in',
						scrollTrigger: {
							trigger: panel,
							start: 'top bottom',
							end: isMobile ? 'top 30%' : `top ${headerH}px`,
							scrub: true,
						}
					});

					gsap.to(caption, {
						autoAlpha: 1,
						duration: 1,
						delay: 2,
						ease: 'power2.in',
						scrollTrigger: {
							trigger: panel,
							start: 'top 35%',
							end: isMobile ? 'top 30%' : `top ${headerH}px`,
							scrub: true,
						}
					});

				}

				// Uscita: il pannello sale verso l'alto mentre il successivo entra
				if (!isLast) {
					gsap.to(panel, {
						yPercent: -100,
						ease: 'none',
						scrollTrigger: {
							trigger: nextPanel,
							start: isMobile ? 'top 40%' : 'top 50%',
							end: 'top top',
							scrub: true,
							// onLeave: () => {
							// 	console.log('sto uscendo');
							// 	this.hideCaption(caption);
							// },
						}
					});
				}

				// Pin del panel
				const pinST = ScrollTrigger.create({
					trigger: panel,
					start: isMobile ? 'top 10%' : `top ${headerH}px`,
					end: isLast ? '+=20%' : undefined,
					pin: true,
					anticipatePin: 0.2,
					pinSpacing: isLast,
				});

				this.triggers.push(pinST);
			});

			// Cleanup automatico al cambio breakpoint
			return () => {
				this.triggers.forEach(st => st.kill());
				this.triggers = [];
				this._hoverCleanups.forEach(fn => fn());
				this._hoverCleanups = [];
				gsap.set(this.panels, { clearProps: 'all' });
			};
		});
	}

	showCaption(caption) {
		gsap.to(caption, { autoAlpha: 1, duration: 1, ease: 'power2.out', delay: 1 });
	}

	hideCaption(caption) {
		const closeFn = this._closeFns.get(caption);
		if (closeFn) closeFn();
		gsap.to(caption, { autoAlpha: 0, duration: 0.3, ease: 'power2.in' });
	}

	initCaptionHover() {
		this.captions.forEach(caption => {

			const btn = caption.querySelector('.fp-btn-plus');
			const title = caption.querySelector('.h5-500gp');
			const text = caption.querySelector('.body-l-500gp');
			let isOpen = false;
			let splitTitle = null;
			let splitText = null;
			let activeTl = null;

			const open = () => {
				if (isOpen) return;
				isOpen = true;

				if (activeTl) { activeTl.kill(); activeTl = null; }

				const targetH = text.offsetHeight + title.offsetHeight + 16 + 19 + 19;

				if (!splitTitle) splitTitle = SplitText.create(title, { type: 'lines', autoSplit: true, mask: 'lines', aria: 'none' });
				if (!splitText) splitText = SplitText.create(text, { type: 'lines', autoSplit: true, mask: 'lines', aria: 'none' });

				// Forza stato iniziale pulito prima di animare
				gsap.set([title, text], { autoAlpha: 1 });
				gsap.set([...splitTitle.lines, ...splitText.lines], { autoAlpha: 0, yPercent: 20 });

				activeTl = gsap.timeline({ onComplete: () => { activeTl = null; } })
					.to(caption, {
						height: targetH,
						width: window.innerWidth > 1300 ? 500 : window.innerWidth > 600 ? 400 : 'calc(100vw - 44px)',
						duration: 0.4,
						ease: 'expo.out',
					}, 0)
					.to(splitTitle.lines, {
						autoAlpha: 1,
						yPercent: 0,
						duration: 0.4,
						ease: 'power2.out',
						stagger: 0.06,
					}, 0.2)
					.to(splitText.lines, {
						autoAlpha: 1,
						yPercent: 0,
						duration: 0.4,
						ease: 'power2.out',
						stagger: 0.04,
					}, 0.2);
			};

			const close = () => {
				if (!isOpen) return;
				isOpen = false;

				if (activeTl) { activeTl.kill(); activeTl = null; }

				const lines = [...(splitTitle?.lines ?? []), ...(splitText?.lines ?? [])];

				activeTl = gsap.timeline({
					onComplete: () => {
						// Hard reset: nasconde tutto e rimuove stili inline
						gsap.set(lines, { autoAlpha: 0, clearProps: 'yPercent' });
						gsap.set([title, text], { autoAlpha: 0 });
						gsap.set(caption, { clearProps: 'width,height' });
						activeTl = null;
					}
				})
					.to(lines, {
						autoAlpha: 0,
						yPercent: -10,
						duration: 0.25,
						ease: 'power2.in',
						stagger: 0.02,
					}, 0)
					.to(caption, {
						width: 0,
						height: 0,
						duration: 0.3,
						ease: 'expo.in',
					}, 0.2);
			};

			const onEnter = () => open();
			const onLeave = () => close();

			// Salva close() per questa caption (usato da hideCaption)
			this._closeFns.set(caption, close);

		if (window.innerWidth > 1024) {
			caption.addEventListener('mouseenter', onEnter);
			caption.addEventListener('mouseleave', onLeave);
		} else {
			caption.addEventListener('click', () => isOpen ? close() : open());
		}

			// Registra cleanup
			this._hoverCleanups.push(() => {
				onLeave();
				caption.removeEventListener('mouseenter', onEnter);
				caption.removeEventListener('mouseleave', onLeave);
				splitTitle?.revert();
				splitText?.revert();
			});

		})
	}

	initScrollClose() {
		this._onScrollStart = () => {
			this._closeFns.forEach(close => close());
		};
		ScrollTrigger.addEventListener('scrollStart', this._onScrollStart);
	}

	initResize() {
		let resizeTimer;
		this._onResize = () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				ScrollTrigger.refresh();
			}, 200);
		};
		window.addEventListener('resize', this._onResize);
	}
}
